import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["isExpired", "questions"];
import http from 'hub-http/clients/apiClient';
import PortalIdParser from 'PortalIdParser';
import { dataFetchingClient } from '../../utils/DataFetchingClient';
import { registerMutation, unwrapHubHttpError } from 'academy-common-lib/utils/DataFetchingClient';
import { BASE_URI_PROGRESS } from '../../constants/ApiConstants';
import { useCallback, useMemo } from 'react';
import { updateCache } from '../../utils/DataFetchingClientCache.new';
import maxBy from 'hs-lodash/maxBy';
import findIndex from 'hs-lodash/findIndex';
import { useFullChirpTrack } from '../tracks/tracksQueries';
import { getPlaylistIdFromUrl } from '../../utils/UrlParamUtils';
import remove from 'hs-lodash/remove';
import { useGetUserProgressQuery, useGetUserTrackProgressQuery, useGetUserLessonProgressQuery } from '../../../__generated__/chirp/com/hubspot/userprogress/bff/rpc/UserProgressBFFChirpService.hooks';
import { useMarkContentCompletedMutation } from '../../../__generated__/chirp/com/hubspot/academy/user/progress/rpc/UserProgressWriterChirpService.hooks';
import { MARK_CONTENT_COMPLETED_MUTATION } from '../../../__generated__/chirp/com/hubspot/academy/user/progress/rpc/UserProgressWriterChirpService.dfc.new';
import { MY_LEARNING_QUERY_FIELD_NAME } from '../myLearning/myLearningQueries';
import { GET_USER_TRACK_PROGRESS_QUERY, GET_USER_LESSON_PROGRESS_QUERY, GET_ALL_USER_PROGRESS_BY_USER_ID_QUERY } from '../../../__generated__/chirp/com/hubspot/userprogress/bff/rpc/UserProgressBFFChirpService.dfc.new';
import { convertToSupportedLanguage } from 'academy-common-lib/constants/academySupportedLanguages';
import { useStartProgressMutation } from '../../../__generated__/chirp/com/hubspot/academy/user/progress/rpc/UserProgressWriterChirpService.hooks';
import { GET_PLAYLIST_PROGRESS } from 'academy-common-lib/features/userProgress/userProgressQueries';
export { usePlaylistProgress, useAllProgress, useStartTrackProgress, useStartLessonProgress, useAssetStatus } from 'academy-common-lib/features/userProgress/userProgressQueries';
/** Cache updator functions */

export const deleteContentProgressFromCache = ({
  trackId,
  lessonId,
  contentId
}) => {
  updateCache(GET_USER_LESSON_PROGRESS_QUERY, lessonProgress => {
    remove(lessonProgress.contentProgress, c => c.contentId === contentId);
  }, {
    lessonId
  });
  if (trackId) updateCache(GET_USER_TRACK_PROGRESS_QUERY, trackProgress => {
    const lessonProgress = trackProgress.progress.find(p => p.lessonId === lessonId);
    if (!lessonProgress) return;
    remove(lessonProgress.contentProgress, c => c.contentId === contentId);
  }, {
    trackId,
    includeExpiredAwards: true
  });
};
const updateLessonProgressWithContentProgress = (lessonProgress, contentId, status) => {
  const content = lessonProgress.contentProgress.find(c => c.contentId === contentId);
  if (content) {
    content.progressType = status;
  } else {
    lessonProgress.contentProgress.push({
      contentId,
      progressType: status,
      updatedAt: new Date().getTime()
    });
  }
};
export const setContentProgressStatusCache = ({
  trackId,
  lessonId,
  contentId,
  status
}) => {
  updateCache(GET_USER_LESSON_PROGRESS_QUERY, lessonProgress => {
    updateLessonProgressWithContentProgress(lessonProgress, contentId, status);
  }, {
    lessonId
  });
  if (trackId) updateCache(GET_USER_TRACK_PROGRESS_QUERY, trackProgress => {
    const lessonProgress = trackProgress.progress.find(p => p.lessonId === lessonId);
    if (!lessonProgress) return;
    updateLessonProgressWithContentProgress(lessonProgress, contentId, status);
  }, {
    trackId,
    includeExpiredAwards: true
  });
};
const convertAssessmentsExamAttemptToChirpExamAttempt = (attemptData, language, userId, portalId) => {
  var _attemptData$attemptF, _attemptData$attemptR, _attemptData$score, _attemptData$passed;
  // ExamAttemptService.ExamAttemptResponse omits userId/portalId/examId/finished
  // fields that the UserProgressBFF-shaped cache expects, and renames `expired`
  // to `isExpired`. Synthesize examId from contentId (exams are content), derive
  // finished from attemptFinishTime, and take userId/portalId from the cache's
  // existing entry for this track (populated by prior BFF reads) with a
  // PortalIdParser fallback for portalId.
  const {
      isExpired,
      questions
    } = attemptData,
    rest = _objectWithoutPropertiesLoose(attemptData, _excluded);
  return Object.assign({}, rest, {
    language,
    userId,
    portalId,
    examId: attemptData.contentId,
    finished: attemptData.attemptFinishTime != null,
    attemptFinishTime: (_attemptData$attemptF = attemptData.attemptFinishTime) !== null && _attemptData$attemptF !== void 0 ? _attemptData$attemptF : undefined,
    attemptRetakeTime: (_attemptData$attemptR = attemptData.attemptRetakeTime) !== null && _attemptData$attemptR !== void 0 ? _attemptData$attemptR : undefined,
    score: (_attemptData$score = attemptData.score) !== null && _attemptData$score !== void 0 ? _attemptData$score : undefined,
    passed: (_attemptData$passed = attemptData.passed) !== null && _attemptData$passed !== void 0 ? _attemptData$passed : undefined,
    expired: isExpired !== null && isExpired !== void 0 ? isExpired : undefined,
    questions: questions.map(question => {
      var _question$contentId, _question$partiallyCo, _question$displayOrde, _question$language, _question$image, _question$imageAlt;
      return Object.assign({}, question, {
        contentId: (_question$contentId = question.contentId) !== null && _question$contentId !== void 0 ? _question$contentId : undefined,
        partiallyCorrectResponse: (_question$partiallyCo = question.partiallyCorrectResponse) !== null && _question$partiallyCo !== void 0 ? _question$partiallyCo : undefined,
        displayOrder: (_question$displayOrde = question.displayOrder) !== null && _question$displayOrde !== void 0 ? _question$displayOrde : undefined,
        language: (_question$language = question.language) !== null && _question$language !== void 0 ? _question$language : undefined,
        image: (_question$image = question.image) !== null && _question$image !== void 0 ? _question$image : undefined,
        imageAlt: (_question$imageAlt = question.imageAlt) !== null && _question$imageAlt !== void 0 ? _question$imageAlt : undefined,
        answers: question.answers.map(answer => {
          var _answer$displayOrder, _answer$correct;
          return Object.assign({}, answer, {
            displayOrder: (_answer$displayOrder = answer.displayOrder) !== null && _answer$displayOrder !== void 0 ? _answer$displayOrder : undefined,
            correct: (_answer$correct = answer.correct) !== null && _answer$correct !== void 0 ? _answer$correct : undefined
          });
        })
      });
    })
  });
};
export const setMostRecentChirpExamAttemptCache = (trackId, attemptData, language) => {
  updateCache(GET_USER_TRACK_PROGRESS_QUERY, trackProgress => {
    var _trackProgress$examAt, _existingAttempt$user, _ref, _existingAttempt$port;
    const existingAttempt = (_trackProgress$examAt = trackProgress.examAttempts[attemptData.id]) !== null && _trackProgress$examAt !== void 0 ? _trackProgress$examAt : Object.values(trackProgress.examAttempts)[0];
    const userId = (_existingAttempt$user = existingAttempt === null || existingAttempt === void 0 ? void 0 : existingAttempt.userId) !== null && _existingAttempt$user !== void 0 ? _existingAttempt$user : 0;
    const portalId = (_ref = (_existingAttempt$port = existingAttempt === null || existingAttempt === void 0 ? void 0 : existingAttempt.portalId) !== null && _existingAttempt$port !== void 0 ? _existingAttempt$port : PortalIdParser.get()) !== null && _ref !== void 0 ? _ref : 0;
    trackProgress.examAttempts[attemptData.id] = convertAssessmentsExamAttemptToChirpExamAttempt(attemptData, language, userId, portalId);
    trackProgress.examIdToRecentAttemptId[attemptData.contentId] = attemptData.id;
  }, {
    trackId,
    includeExpiredAwards: true
  });
};
export const addQuestionSubmissionCache = (trackId, attemptId, questionId, answerIds) => {
  updateCache(GET_USER_TRACK_PROGRESS_QUERY, trackProgress => {
    trackProgress.examAttempts[attemptId].questionSubmissions = [...trackProgress.examAttempts[attemptId].questionSubmissions, {
      questionId,
      answerIds
    }];
  }, {
    trackId,
    includeExpiredAwards: true
  });
};
export const setLessonQuizAttemptCache = (lessonId, quizId, attemptData, trackId) => {
  updateCache(GET_USER_LESSON_PROGRESS_QUERY, lessonProgress => {
    var _attemptData$attemptF2, _attemptData$passed2;
    lessonProgress.quizIdToRecentAttemptId[quizId] = attemptData.id;
    lessonProgress.quizAttempts[attemptData.id] = Object.assign({}, attemptData, {
      attemptFinishTime: (_attemptData$attemptF2 = attemptData.attemptFinishTime) !== null && _attemptData$attemptF2 !== void 0 ? _attemptData$attemptF2 : undefined,
      passed: (_attemptData$passed2 = attemptData.passed) !== null && _attemptData$passed2 !== void 0 ? _attemptData$passed2 : undefined
    });
  }, {
    lessonId
  });
  if (trackId) updateCache(GET_USER_TRACK_PROGRESS_QUERY, trackProgress => {
    var _attemptData$attemptF3, _attemptData$passed3;
    const lessonProgress = trackProgress.progress.find(p => p.lessonId === lessonId);
    if (!lessonProgress) return;
    lessonProgress.quizIdToRecentAttemptId[quizId] = attemptData.id;
    lessonProgress.quizAttempts[attemptData.id] = Object.assign({}, attemptData, {
      attemptFinishTime: (_attemptData$attemptF3 = attemptData.attemptFinishTime) !== null && _attemptData$attemptF3 !== void 0 ? _attemptData$attemptF3 : undefined,
      passed: (_attemptData$passed3 = attemptData.passed) !== null && _attemptData$passed3 !== void 0 ? _attemptData$passed3 : undefined
    });
  }, {
    trackId,
    includeExpiredAwards: true
  });
};
export const clearLessonQuizAttemptCache = ({
  trackId,
  lessonId,
  quizId,
  attemptId
}) => {
  updateCache(GET_USER_LESSON_PROGRESS_QUERY, lessonProgress => {
    delete lessonProgress.quizIdToRecentAttemptId[quizId];
    delete lessonProgress.quizAttempts[attemptId];
  }, {
    lessonId
  });
  if (trackId) updateCache(GET_USER_TRACK_PROGRESS_QUERY, trackProgress => {
    const lessonProgress = trackProgress.progress.find(p => p.lessonId === lessonId);
    if (!lessonProgress) return;
    delete lessonProgress.quizIdToRecentAttemptId[quizId];
    delete lessonProgress.quizAttempts[attemptId];
  }, {
    trackId,
    includeExpiredAwards: true
  });
};
const updateLessonProgressWithQuizAttemptAnswer = (lessonProgress, attemptId, questionId, answerIds, correctAnswer) => {
  lessonProgress.quizAttempts[attemptId].answerSubmissions.push({
    quizAttemptId: attemptId,
    id: 0,
    questionId,
    answerIds,
    correctAnswer,
    submissionTime: new Date().getTime()
  });
};
export const addLessonQuizAttemptAnswerCache = (lessonId, attemptId, questionId, answerIds, correctAnswer, trackId) => {
  updateCache(GET_USER_LESSON_PROGRESS_QUERY, lessonProgress => {
    updateLessonProgressWithQuizAttemptAnswer(lessonProgress, attemptId, questionId, answerIds, correctAnswer);
  }, {
    lessonId
  });
  if (trackId) updateCache(GET_USER_TRACK_PROGRESS_QUERY, trackProgress => {
    const lessonProgress = trackProgress.progress.find(p => p.lessonId === lessonId);
    if (!lessonProgress) return;
    updateLessonProgressWithQuizAttemptAnswer(lessonProgress, attemptId, questionId, answerIds, correctAnswer);
  }, {
    trackId,
    includeExpiredAwards: true
  });
};
const updateLessonProgressWithStartQuizAttempt = (lessonProgress, attemptId, questionId, answerIds, correctAnswer, startQuizInfo) => {
  lessonProgress.quizIdToRecentAttemptId[startQuizInfo.quizId] = attemptId;
  lessonProgress.quizAttempts[attemptId] = Object.assign({}, startQuizInfo.firstAttemptData, {
    answerSubmissions: [...startQuizInfo.firstAttemptData.answerSubmissions, {
      quizAttemptId: attemptId,
      id: 0,
      questionId,
      answerIds,
      correctAnswer,
      submissionTime: new Date().getTime()
    }]
  });
};
export const addLessonStartQuizAttemptAnswerCache = (lessonId, attemptId, questionId, answerIds, correctAnswer, startQuizInfo, trackId) => {
  updateCache(GET_USER_LESSON_PROGRESS_QUERY, lessonProgress => {
    updateLessonProgressWithStartQuizAttempt(lessonProgress, attemptId, questionId, answerIds, correctAnswer, startQuizInfo);
  }, {
    lessonId
  });
  if (trackId) updateCache(GET_USER_TRACK_PROGRESS_QUERY, trackProgress => {
    const lessonProgress = trackProgress.progress.find(p => p.lessonId === lessonId);
    if (!lessonProgress) return;
    updateLessonProgressWithStartQuizAttempt(lessonProgress, attemptId, questionId, answerIds, correctAnswer, startQuizInfo);
  }, {
    trackId,
    includeExpiredAwards: true
  });
};
const updateLessonProgressWithQuizAttemptAnswerCache = (lessonProgress, attemptId, questionId) => {
  lessonProgress.quizAttempts[attemptId].answerSubmissions = lessonProgress.quizAttempts[attemptId].answerSubmissions.filter(answer => answer.questionId !== questionId);
};
export const removeLessonQuizAttemptAnswerCache = ({
  trackId,
  lessonId,
  attemptId,
  questionId
}) => {
  updateCache(GET_USER_LESSON_PROGRESS_QUERY, lessonProgress => {
    updateLessonProgressWithQuizAttemptAnswerCache(lessonProgress, attemptId, questionId);
  }, {
    lessonId
  });
  if (trackId) updateCache(GET_USER_TRACK_PROGRESS_QUERY, trackProgress => {
    const lessonProgress = trackProgress.progress.find(p => p.lessonId === lessonId);
    if (!lessonProgress) return;
    updateLessonProgressWithQuizAttemptAnswerCache(lessonProgress, attemptId, questionId);
  }, {
    trackId,
    includeExpiredAwards: true
  });
};

/** Core query hooks */

export const useTrackProgressById = trackId => {
  const {
    data: trackProgressById,
    loading: trackProgressByIdLoading,
    error: trackProgressByIdError,
    refetch: trackProgressByIdRefetch
  } = useGetUserTrackProgressQuery({
    variables: {
      trackId: trackId,
      includeExpiredAwards: true
    },
    skip: !trackId || isNaN(Number(trackId)),
    context: {
      timeout: 25000
    },
    onCompleted(data) {
      data.result.progress.forEach(lessonProgress => {
        updateCache(GET_USER_LESSON_PROGRESS_QUERY, () => lessonProgress, {
          lessonId: lessonProgress.lessonId
        });
      });
    }
  });
  return {
    trackProgressById,
    trackProgressByIdLoading,
    trackProgressByIdError: unwrapHubHttpError(trackProgressByIdError),
    trackProgressByIdRefetch
  };
};
export const useLessonProgressById = lessonId => {
  const {
    data: lessonProgressById,
    loading: lessonProgressByIdLoading,
    error: lessonProgressByIdError,
    refetch: lessonProgressByIdRefetch
  } = useGetUserLessonProgressQuery({
    variables: {
      lessonId: Number(lessonId)
    },
    skip: !lessonId,
    context: {
      timeout: 25000
    }
  });
  return {
    lessonProgressById,
    lessonProgressByIdLoading,
    lessonProgressByIdError: unwrapHubHttpError(lessonProgressByIdError),
    lessonProgressByIdRefetch
  };
};
export const useStartPlaylistProgress = ({
  playlistId,
  language
}) => {
  const playlistIdToUse = playlistId || getPlaylistIdFromUrl();
  const [startPlaylistProgress, {
    loading: startPlaylistProgressLoading,
    called: startPlaylistProgressCalled,
    error: startPlaylistProgressCalledError
  }] = useStartProgressMutation({
    variables: {
      contentId: playlistId,
      contentType: 'PLAYLIST',
      language: convertToSupportedLanguage(language)
    },
    refetchQueries: [{
      query: GET_ALL_USER_PROGRESS_BY_USER_ID_QUERY,
      variables: {
        language: convertToSupportedLanguage(language)
      },
      context: {
        timeout: 25000
      }
    }, ...(playlistIdToUse ? [{
      query: GET_PLAYLIST_PROGRESS,
      variables: {
        playlistId: playlistIdToUse
      }
    }] : [])],
    update: (cache, result) => {
      const {
        data,
        errors
      } = result;
      if (!data || errors && errors.length) return;
      cache.evict({
        fieldName: MY_LEARNING_QUERY_FIELD_NAME
      });
    }
  });
  return [startPlaylistProgress, {
    startPlaylistProgressLoading,
    startPlaylistProgressCalled,
    startPlaylistProgressCalledError
  }];
};
export const START_LESSON_PROGRESS = registerMutation({
  fieldName: 'startLessonProgress',
  args: ['language', 'lessonId', 'playlistId'],
  fetcher({
    language,
    lessonId,
    playlistId
  }) {
    return http.post(`${BASE_URI_PROGRESS}/asset-progress/start/lesson/${lessonId}`, {
      data: {
        playlistId,
        language
      }
    });
  }
});
export const useUpdateContentProgress = () => {
  const [mutate, {
    loading: updateContentProgressLoading,
    called: updateContentProgressCalled,
    error: updateContentProgressError,
    data: updateContentProgressData
  }] = useMarkContentCompletedMutation({
    update: (cache, result) => {
      const {
        data,
        errors
      } = result;
      if (!data || errors && errors.length) return;
      cache.evict({
        fieldName: MY_LEARNING_QUERY_FIELD_NAME
      });
    }
  });
  const updateContentProgress = useCallback(({
    contentId,
    lessonId,
    trackId,
    language
  }) => {
    const playlistId = getPlaylistIdFromUrl();
    return mutate({
      awaitRefetchQueries: true,
      context: {
        timeout: 25000
      },
      variables: {
        contentId,
        lessonId,
        trackId,
        playlistId,
        progressType: 'COMPLETED',
        language: convertToSupportedLanguage(language),
        source: 'ACADEMY'
      },
      refetchQueries: [{
        query: GET_ALL_USER_PROGRESS_BY_USER_ID_QUERY,
        variables: {
          language: convertToSupportedLanguage(language)
        },
        context: {
          timeout: 25000
        }
      }, ...(lessonId ? [{
        query: GET_USER_LESSON_PROGRESS_QUERY,
        variables: {
          lessonId
        }
      }] : []), ...(trackId ? [{
        query: GET_USER_TRACK_PROGRESS_QUERY,
        variables: {
          trackId,
          includeExpiredAwards: true
        }
      }] : []), ...(playlistId ? [{
        query: GET_PLAYLIST_PROGRESS,
        variables: {
          playlistId
        }
      }] : [])]
    });
  }, [mutate]);
  return [updateContentProgress, {
    updateContentProgressLoading,
    updateContentProgressCalled,
    updateContentProgressError,
    updateContentProgressData
  }];
};
export const updateContentProgress = ({
  contentId,
  lessonId,
  trackId,
  language
}) => {
  const playlistId = getPlaylistIdFromUrl();
  return dataFetchingClient.mutate({
    mutation: MARK_CONTENT_COMPLETED_MUTATION,
    context: {
      timeout: 25000
    },
    variables: {
      contentId,
      lessonId,
      trackId,
      playlistId,
      progressType: 'COMPLETED',
      language: convertToSupportedLanguage(language),
      source: 'ACADEMY'
    },
    refetchQueries: [{
      query: GET_ALL_USER_PROGRESS_BY_USER_ID_QUERY,
      variables: {
        language: convertToSupportedLanguage(language)
      },
      context: {
        timeout: 25000
      }
    }, ...(lessonId ? [{
      query: GET_USER_LESSON_PROGRESS_QUERY,
      variables: {
        lessonId
      }
    }] : []), ...(trackId ? [{
      query: GET_USER_TRACK_PROGRESS_QUERY,
      variables: {
        trackId,
        includeExpiredAwards: true
      }
    }] : []), ...(playlistId ? [{
      query: GET_PLAYLIST_PROGRESS,
      variables: {
        playlistId
      }
    }] : [])],
    update: (cache, result) => {
      const {
        data,
        errors
      } = result;
      if (!data || errors && errors.length) return;
      cache.evict({
        fieldName: MY_LEARNING_QUERY_FIELD_NAME
      });
    }
  }).then(result => result.data);
};
export const useTrackRedirectUrl = trackId => {
  const {
    trackProgressById,
    trackProgressByIdLoading,
    trackProgressByIdError
  } = useTrackProgressById(Number(trackId));
  const {
    track,
    trackLoading,
    trackError
  } = useFullChirpTrack(trackId);
  const trackRedirectUrl = useMemo(() => {
    if (!trackProgressById || !track) return null;
    const allContentProgress = [];
    if (track.introVideo) {
      allContentProgress.push({
        lessonId: null,
        contentId: trackProgressById.introVideoProgress && trackProgressById.introVideoProgress.contentId,
        status: trackProgressById.introVideoProgress && trackProgressById.introVideoProgress.progressType,
        updatedAt: trackProgressById.introVideoProgress && trackProgressById.introVideoProgress.updatedAt,
        url: `/tracks/${trackId}/intro`
      });
    }
    track.lessons.forEach(lesson => {
      var _trackProgressById$pr;
      const lessonProgress = (_trackProgressById$pr = trackProgressById.progress) === null || _trackProgressById$pr === void 0 ? void 0 : _trackProgressById$pr.find(l => l.lessonId === lesson.id);
      if (lessonProgress) {
        lesson.content.forEach(content => {
          const contentProgress = lessonProgress.contentProgress.find(c => c.contentId === content.id);
          allContentProgress.push({
            lessonId: lesson.id,
            contentId: content.id,
            status: contentProgress && lessonProgress.status !== 'NOT_STARTED' ? contentProgress.progressType : 'NOT_STARTED',
            updatedAt: contentProgress ? contentProgress.updatedAt : null,
            url: `/tracks/${trackId}/${lesson.id}/${content.id}`
          });
        });
      }
    });
    allContentProgress.push({
      lessonId: null,
      contentId: null,
      status: null,
      updatedAt: null,
      url: `/tracks/${trackId}/${track.exam ? 'exam' : 'complete'}`
    });
    if (trackProgressById.status === 'COMPLETED') {
      return allContentProgress[allContentProgress.length - 1].url;
    }
    const lastCompleted = maxBy(allContentProgress.filter(p => p.status === 'COMPLETED'), p => p.updatedAt);
    if (lastCompleted) {
      const lastCompletedIndex = findIndex(allContentProgress, lastCompleted);
      const unprogressedItemsAfterLastCompletedItem = allContentProgress.slice(lastCompletedIndex + 1).filter(p => p.status !== 'COMPLETED');
      return unprogressedItemsAfterLastCompletedItem[0].url;
    }
    return allContentProgress[0].url;
  }, [track, trackId, trackProgressById]);
  return {
    trackRedirectUrl,
    trackRedirectUrlLoading: trackProgressByIdLoading || trackLoading,
    trackRedirectUrlError: trackProgressByIdError || trackError
  };
};
export const useTrackProgress = (trackId, options = {}) => {
  const {
    data: userProgressData,
    loading,
    error
  } = useGetUserProgressQuery({
    variables: {
      requestedProgress: [{
        id: trackId,
        type: 'TRACK'
      }]
    },
    fetchPolicy: options.fetchPolicy,
    pollInterval: options.polling ? 1000 : 0,
    skip: options.skip,
    context: {
      timeout: options.timeout
    }
  });
  const trackProgress = userProgressData === null || userProgressData === void 0 ? void 0 : userProgressData.progress.find(progress => progress.type === 'TRACK');
  return {
    trackProgress,
    trackProgressLoading: loading,
    trackProgressError: unwrapHubHttpError(error)
  };
};